import { createContext, useContext } from 'react';
import type { Company, MemberRole } from '@/types/database';

// O objeto de contexto e o hook vivem separados do CompanyProvider
// (CompanyContext.tsx) de propósito: um arquivo que exporta tanto um componente
// quanto valores não-componente quebra o React Fast Refresh, fazendo o
// `useContext` retornar `undefined` no meio de um hot-reload e disparar
// "useCompany must be used within a CompanyProvider" em dev. Mantendo o
// Provider como único export do .tsx, o Fast Refresh volta a funcionar.

export interface CompanyContextType {
  companies: Company[];
  activeCompany: Company | null;
  loading: boolean;
  /** Role of the current user within the active company (team accounts). */
  currentRole: MemberRole | null;
  /** Map of company id -> the current user's role in that company. */
  roleByCompany: Record<string, MemberRole>;
  setActiveCompanyId: (id: string) => void;
  refreshCompanies: () => Promise<void>;
  createCompany: (company: Omit<Company, 'id' | 'user_id' | 'created_at'>) => Promise<Company | null>;
  updateCompany: (id: string, company: Partial<Company>) => Promise<boolean>;
  deleteCompany: (id: string) => Promise<boolean>;
  canCreateCompany: boolean;
}

export const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export function useCompany() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
